import { openDB, DBSchema } from "idb"

/**
 * A track held in IndexedDB. Only recordings live here for long: once a track
 * is saved (status "completed") it is published into PowerSync's synced
 * `tracks` table and this record is deleted — see publishCompletedTracks in
 * lib/tracks/publishTracks.ts. A completed record that is still here is just
 * waiting for that local hand-off (or for its owner to sign in).
 */
export type Track = {
  id: string
  timestamp: number
  projectId: string
  /** "paused" is retained so unfinished tracks created by older realtime
   *  filter builds remain recoverable. New recordings use active/completed. */
  status: "active" | "paused" | "completed"
  /** The user who was signed in when this track was recorded. Undefined on
   *  tracks recorded before ownership tracking existed ("legacy" tracks) —
   *  see claimTrackOwner. Publishing must never attribute a track to
   *  whoever happens to be signed in when the hand-off runs. */
  ownerId?: string
  /** Set by completeTrack / replaceTrackWithCompletedSegments each time this
   *  record is (re)completed. Distinguishes the generation of a track that
   *  was reopened by reopenTrackForNativeRecovery and completed again under
   *  the same id, so a publish of the earlier generation can't delete the
   *  new one — see deleteCompletedTrackGeneration. */
  completedAt?: number
  /** @deprecated Device-local "show on map" now lives in
   *  lib/trackDisplayPrefs. Only read to carry an old choice across when a
   *  legacy record is published. */
  displayOnMap?: boolean
  /** @deprecated Set by builds that uploaded straight to Supabase. A record
   *  with this set is already on the server, so publishing just drops it. */
  uploadedAt?: number
  /** @deprecated The Supabase row id those builds uploaded to, or `null`
   *  when there was nothing to upload. Only meaningful with `uploadedAt`. */
  remoteId?: string | null
}

export type TrackPoint = {
  timestamp: number
  trackId: string
  point: [x: number, y: number]
  /** Reported geolocation speed in metres per second; null means unknown. */
  speedMps?: number | null
  sourceId?: string
  sourceSessionId?: string
  sourceSequence?: number
}

export type NativeTrackPointSource = {
  sourceSessionId: string
  sourceSequence: number
}

interface TracksDb extends DBSchema {
  tracks: {
    key: string
    value: Track
    indexes: {
      timestamp: number
      projectId: string
      "projectId-timestamp": [string, number]
      status: string
    }
  }
  trackPoints: {
    key: number // Auto-incrementing key
    value: TrackPoint
    indexes: {
      timestamp: number
      trackId: string
      sourceId: string
    }
  }
}

// Initialize the DB
export const tracksDb = openDB<TracksDb>("track-store", 2, {
  upgrade(db, oldVersion, _newVersion, transaction) {
    if (oldVersion < 1) {
      const trackStore = db.createObjectStore("tracks", { keyPath: "id" })
      trackStore.createIndex("timestamp", "timestamp")
      trackStore.createIndex("projectId", "projectId")
      trackStore.createIndex("projectId-timestamp", ["projectId", "timestamp"])
      trackStore.createIndex("status", "status")

      const trackPointsStore = db.createObjectStore("trackPoints", {
        autoIncrement: true, // Use auto-incrementing key instead of keyPath
      })
      trackPointsStore.createIndex("timestamp", "timestamp")
      trackPointsStore.createIndex("trackId", "trackId")
    }

    if (oldVersion < 2) {
      transaction
        .objectStore("trackPoints")
        .createIndex("sourceId", "sourceId", { unique: true })
    }
  },
})

export const getNativeTrackPointSourceId = ({
  sourceSessionId,
  sourceSequence,
}: NativeTrackPointSource) => `${sourceSessionId}:${sourceSequence}`

const getNativeSourceFields = (source: NativeTrackPointSource) => ({
  sourceId: getNativeTrackPointSourceId(source),
  sourceSessionId: source.sourceSessionId,
  sourceSequence: source.sourceSequence,
})

export const getLatestTrackForProject = async (projectId: string) => {
  return new Promise<Track | undefined>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("IndexedDB operation timed out"))
    }, 2000)

    tracksDb
      .then(async (db) => {
        const tx = db.transaction("tracks", "readonly")
        const index = tx.objectStore("tracks").index("projectId-timestamp")

        const cursor = await index.openCursor(
          IDBKeyRange.bound([projectId, 0], [projectId, Infinity]),
          "prev",
        )

        await tx.done
        clearTimeout(timeoutId)
        resolve(cursor?.value)
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        reject(error)
      })
  })
}

export const getAllTracksForProject = async (projectId: string) => {
  const db = await tracksDb
  const tx = db.transaction("tracks", "readonly")
  const index = tx.objectStore("tracks").index("projectId-timestamp")

  let cursor = await index.openCursor(
    IDBKeyRange.bound([projectId, 0], [projectId, Infinity]),
    "prev", // Newest first
  )

  const tracks: Track[] = []
  while (cursor) {
    tracks.push(cursor.value)
    cursor = await cursor.continue()
  }

  await tx.done

  return tracks
}

export const getTrack = (id: string) => {
  return new Promise<Track | undefined>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("IndexedDB operation timed out"))
    }, 2000)

    tracksDb
      .then((db) => db.get("tracks", id))
      .then((track) => {
        clearTimeout(timeoutId)
        resolve(track)
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        reject(error)
      })
  })
}

export const getActiveTrack = async () => {
  const db = await tracksDb
  const tx = db.transaction("tracks", "readonly")
  const index = tx.objectStore("tracks").index("status")

  const active = await index.get(IDBKeyRange.only("active"))
  const paused = active ?? (await index.get(IDBKeyRange.only("paused")))

  await tx.done

  return paused ?? null
}

export const appendTrackPoint = async (
  trackId: string,
  point: [x: number, y: number],
  timestamp = Date.now(),
  speedMps?: number | null,
) => {
  const db = await tracksDb
  const tx = db.transaction(["tracks", "trackPoints"], "readwrite")
  const track = await tx.objectStore("tracks").get(trackId)

  if (track?.status !== "active") {
    await tx.done
    throw new Error(
      `Cannot append point to ${track ? track.status : "missing"} track ${trackId}`,
    )
  }

  const trackPoint = {
    trackId,
    point,
    timestamp,
    ...(speedMps !== undefined ? { speedMps } : {}),
  }
  await tx.objectStore("trackPoints").add(trackPoint)
  await tx.done
  return trackPoint
}

export const appendNativeTrackPoint = async (
  trackId: string,
  point: [x: number, y: number],
  timestamp: number,
  source: NativeTrackPointSource,
  speedMps?: number | null,
) => {
  const db = await tracksDb
  const tx = db.transaction(["tracks", "trackPoints"], "readwrite")
  const trackPointsStore = tx.objectStore("trackPoints")
  const sourceId = getNativeTrackPointSourceId(source)
  const existing = await trackPointsStore.index("sourceId").get(sourceId)

  if (existing) {
    await tx.done
    if (existing.trackId !== trackId) {
      throw new Error(
        "Native location source is already assigned to another track",
      )
    }
    return existing
  }

  const track = await tx.objectStore("tracks").get(trackId)
  if (track?.status !== "active") {
    await tx.done
    throw new Error(
      `Cannot append point to ${track ? track.status : "missing"} track ${trackId}`,
    )
  }

  const trackPoint = {
    trackId,
    point,
    timestamp,
    ...(speedMps !== undefined ? { speedMps } : {}),
    ...getNativeSourceFields(source),
  }
  await trackPointsStore.add(trackPoint)
  await tx.done
  return trackPoint
}

export type CreateTrackOptions = {
  point?: [x: number, y: number]
  timestamp?: number
  source?: NativeTrackPointSource
  speedMps?: number | null
  /** The signed-in user recording this track, if any. Recording proceeds
   *  without one (see useActiveTrack.startTrack) — an ownerless track is
   *  claimed by whoever is signed in when it's later published (see
   *  claimTrackOwner), matching pre-ownership-tracking behaviour. */
  ownerId?: string
}

export const createTrack = async (
  projectId: string,
  options: CreateTrackOptions = {},
) => {
  const { point, timestamp = Date.now(), source, speedMps, ownerId } = options
  const trackId = crypto.randomUUID()
  const db = await tracksDb
  const tx = db.transaction(["tracks", "trackPoints"], "readwrite")
  const trackData = {
    id: trackId,
    projectId,
    status: "active" as const,
    timestamp,
    ...(ownerId !== undefined ? { ownerId } : {}),
  }
  const tracksStore = tx.objectStore("tracks")
  const trackPointsStore = tx.objectStore("trackPoints")

  if (source) {
    const existingPoint = await trackPointsStore
      .index("sourceId")
      .get(getNativeTrackPointSourceId(source))
    if (existingPoint) {
      const existingTrack = await tracksStore.get(existingPoint.trackId)
      if (existingTrack) {
        await tx.done
        return existingTrack
      }
    }
  }

  await tracksStore.add(trackData)
  if (point) {
    await trackPointsStore.add({
      trackId,
      point,
      timestamp,
      ...(speedMps !== undefined ? { speedMps } : {}),
      ...(source ? getNativeSourceFields(source) : {}),
    })
  }
  await tx.done
  return trackData
}

/**
 * Reopen one completed local track for an explicitly matched native session.
 * The track key and project are both checked before changing only its status;
 * points and all other track metadata remain untouched.
 */
export const reopenTrackForNativeRecovery = async (
  trackId: string,
  projectId: string,
): Promise<Track> => {
  const db = await tracksDb
  const tx = db.transaction(["tracks", "trackPoints"], "readwrite")
  const tracksStore = tx.objectStore("tracks")
  const track = await tracksStore.get(trackId)

  if (!track) {
    await tx.done
    throw new Error("Cannot recover a missing track")
  }
  if (track.projectId !== projectId) {
    await tx.done
    throw new Error("Cannot recover a track from another project")
  }
  if (track.status !== "completed") {
    await tx.done
    throw new Error("Only a completed track can be reopened for recovery")
  }

  const pointCount = await tx
    .objectStore("trackPoints")
    .index("trackId")
    .count(trackId)
  if (pointCount >= 2) {
    await tx.done
    throw new Error(
      "A completed track with two or more points cannot be reopened for recovery",
    )
  }

  // Clear this generation's completion/upload fields along with reopening —
  // the reopened track will be re-completed with a fresh `completedAt` (see
  // completeTrack), which is what lets deleteCompletedTrackGeneration tell an
  // in-flight publish of the old generation apart from the new one.
  const {
    uploadedAt: _uploadedAt,
    remoteId: _remoteId,
    completedAt: _completedAt,
    ...rest
  } = track
  const reopened: Track = { ...rest, status: "active" }
  await tracksStore.put(reopened)
  await tx.done
  return reopened
}

/**
 * Mark a raw recording as saved (ready to be published to PowerSync) and
 * return its persisted value.
 * Keeping this in the database module means a save-as-is operation has the
 * same missing-track and status checks as the transactional filtered path.
 */
export const completeTrack = async (trackId: string): Promise<Track> => {
  const db = await tracksDb
  const tx = db.transaction("tracks", "readwrite")
  const track = await tx.objectStore("tracks").get(trackId)

  if (!track) {
    await tx.done
    throw new Error(`Track ${trackId} not found`)
  }

  // A track reopened by reopenTrackForNativeRecovery can be completed again
  // under this same id. Its point set may have grown since any previous
  // upload outcome, so that outcome must not carry over — drop it and let
  // the track be published afresh. completedAt is stamped fresh so an
  // in-flight publish of the previous generation can be told apart from
  // this one (see deleteCompletedTrackGeneration).
  const { uploadedAt: _uploadedAt, remoteId: _remoteId, ...rest } = track
  const completed: Track = {
    ...rest,
    status: "completed",
    completedAt: Date.now(),
  }
  await tx.objectStore("tracks").put(completed)
  await tx.done
  return completed
}

export type TrackOutputSegment = Array<
  Pick<
    TrackPoint,
    | "point"
    | "timestamp"
    | "speedMps"
    | "sourceId"
    | "sourceSessionId"
    | "sourceSequence"
  >
>

/**
 * Atomically replace a raw track with completed output segments.
 *
 * The caller does the pure filtering before entering this operation. Raw
 * points are removed before output points are inserted so the unique native
 * sourceId index can be preserved when a point moves to an output segment.
 * IndexedDB aborts the whole transaction if any insert fails, leaving the raw
 * track available for retry/recovery.
 */
export const replaceTrackWithCompletedSegments = async (
  rawTrackId: string,
  segments: TrackOutputSegment[],
): Promise<Track[]> => {
  const db = await tracksDb
  const tx = db.transaction(["tracks", "trackPoints"], "readwrite")
  const tracksStore = tx.objectStore("tracks")
  const trackPointsStore = tx.objectStore("trackPoints")
  const rawTrack = await tracksStore.get(rawTrackId)

  if (!rawTrack) {
    await tx.done
    throw new Error(`Track ${rawTrackId} not found`)
  }

  if (rawTrack.status === "completed") {
    await tx.done
    throw new Error(`Track ${rawTrackId} is already completed`)
  }

  try {
    const rawPointsIndex = trackPointsStore.index("trackId")
    let rawPointCursor = await rawPointsIndex.openCursor(
      IDBKeyRange.only(rawTrackId),
    )
    while (rawPointCursor) {
      await rawPointCursor.delete()
      rawPointCursor = await rawPointCursor.continue()
    }
    await tracksStore.delete(rawTrackId)

    const outputTracks: Track[] = []
    for (const segment of segments) {
      if (segment.length === 0) continue

      const outputTrack: Track = {
        id: crypto.randomUUID(),
        projectId: rawTrack.projectId,
        status: "completed",
        timestamp: segment[0].timestamp,
        completedAt: Date.now(),
        ...(rawTrack.displayOnMap !== undefined
          ? { displayOnMap: rawTrack.displayOnMap }
          : {}),
        ...(rawTrack.ownerId !== undefined
          ? { ownerId: rawTrack.ownerId }
          : {}),
      }
      await tracksStore.add(outputTrack)

      for (const segmentPoint of segment) {
        await trackPointsStore.add({
          ...segmentPoint,
          trackId: outputTrack.id,
        })
      }
      outputTracks.push(outputTrack)
    }

    await tx.done
    return outputTracks
  } catch (error) {
    // An exception from application code (rather than an IDB request) does
    // not necessarily abort an idb transaction. Explicitly abort here so a
    // partially transformed raw track can never be committed.
    try {
      tx.abort()
    } catch {
      // The transaction may already be in its aborting/finished state.
    }
    try {
      await tx.done
    } catch {
      // Expected when abort() is successful; preserve the original error.
    }
    throw error
  }
}

/**
 * Claim an ownerless ("legacy") track for the current user. Only writes
 * when the track has no owner yet — once claimed, a different user can't
 * also claim it. This matches pre-ownership-tracking behaviour, where the
 * persisted upload mutation simply ran under whoever was signed in when it
 * fired; it's a deliberate trade-off (the wrong signed-in user "claiming" an
 * old track by triggering a pass) rather than leaving legacy tracks stuck
 * unpublished forever. Returns the current stored track either way (claimed, or
 * unchanged if it already had an owner or no longer exists).
 */
export const claimTrackOwner = async (
  trackId: string,
  ownerId: string,
): Promise<Track | undefined> => {
  const db = await tracksDb
  const tx = db.transaction("tracks", "readwrite")
  const store = tx.objectStore("tracks")
  const track = await store.get(trackId)

  if (!track || track.ownerId !== undefined) {
    await tx.done
    return track
  }

  const claimed: Track = { ...track, ownerId }
  await store.put(claimed)
  await tx.done
  return claimed
}

/**
 * Completed tracks still held in IndexedDB — the ones the publish pass has yet
 * to hand over to PowerSync. Reads straight off the `status` index, so a pass
 * is safe to run from a cold start with no in-memory state.
 */
export const getCompletedTracks = async (): Promise<Track[]> => {
  const db = await tracksDb
  return db.getAllFromIndex("tracks", "status", IDBKeyRange.only("completed"))
}

/**
 * Delete a completed track and its points once it has been handed to
 * PowerSync — but only if the stored record is still the generation that was
 * published (still `status: "completed"`, same `completedAt`). A track
 * reopened by reopenTrackForNativeRecovery and completed again while the
 * publish was in flight has moved on, and its newer points haven't been
 * published yet, so it must be left for the next pass. Returns whether the
 * track was deleted.
 */
export const deleteCompletedTrackGeneration = async (
  trackId: string,
  completedAt: number | undefined,
): Promise<boolean> => {
  const db = await tracksDb
  const tx = db.transaction(["tracks", "trackPoints"], "readwrite")
  const tracksStore = tx.objectStore("tracks")
  const track = await tracksStore.get(trackId)

  if (
    !track ||
    track.status !== "completed" ||
    track.completedAt !== completedAt
  ) {
    await tx.done
    return false
  }

  await tracksStore.delete(trackId)
  let cursor = await tx
    .objectStore("trackPoints")
    .index("trackId")
    .openCursor(IDBKeyRange.only(trackId))
  while (cursor) {
    await cursor.delete()
    cursor = await cursor.continue()
  }

  await tx.done
  return true
}

/**
 * All points of a track in insertion order. The UI re-reads this after every
 * recorded batch, so it uses a single getAll request rather than a cursor,
 * which costs an async round trip per point on long tracks.
 */
export const getTrackPoints = async (trackId: string) => {
  const db = await tracksDb
  return db.getAllFromIndex("trackPoints", "trackId", IDBKeyRange.only(trackId))
}

export const deleteTrack = async (trackId: string) => {
  const db = await tracksDb
  await db.delete("tracks", trackId)
}

export const deleteTrackPointsAfter = async (
  trackId: string,
  timestamp: number,
) => {
  const db = await tracksDb
  const tx = db.transaction("trackPoints", "readwrite")
  const trackPointsIndex = tx.objectStore("trackPoints").index("trackId")

  let cursor = await trackPointsIndex.openCursor(IDBKeyRange.only(trackId))
  while (cursor) {
    if (cursor.value.timestamp > timestamp) {
      await cursor.delete()
    }
    cursor = await cursor.continue()
  }

  await tx.done
}

export const deleteTrackPointsAtOrAfter = async (
  trackId: string,
  timestamp: number,
) => {
  const db = await tracksDb
  const tx = db.transaction("trackPoints", "readwrite")
  const trackPointsIndex = tx.objectStore("trackPoints").index("trackId")

  let cursor = await trackPointsIndex.openCursor(IDBKeyRange.only(trackId))
  while (cursor) {
    if (cursor.value.timestamp >= timestamp) {
      await cursor.delete()
    }
    cursor = await cursor.continue()
  }

  await tx.done
}

export const deleteTrackWithPoints = async (trackId: string) => {
  const db = await tracksDb
  const tx = db.transaction(["tracks", "trackPoints"], "readwrite")
  const trackPointsIndex = tx.objectStore("trackPoints").index("trackId")

  await tx.objectStore("tracks").delete(trackId)

  let cursor = await trackPointsIndex.openCursor(IDBKeyRange.only(trackId))
  while (cursor) {
    await cursor.delete()
    cursor = await cursor.continue()
  }

  await tx.done
}
